/**
 * Short handbook-style Info blurbs for Specified Systems (MBIE Compliance Schedule Handbook).
 * Used on Building → CS SS hub Info panels. Not a substitute for the full PDF.
 */
const BY_CODE = {
  '1': {
    title: 'SS 1 — Automatic systems for fire suppression',
    body: 'Inspect and maintain so the system continues to meet its performance standard (e.g. NZS 4541 for sprinklers). Annual Form 12A coverage is typical; follow the building CS procedures and interface tests listed on the schedule.',
  },
  '1.1': {
    title: 'SS 1.1 — Automatic sprinkler systems',
    body: 'Sprinkler systems must be inspected and maintained to the performance standard named on the CS. Check valves, water supplies, alarms, and interfaces with other SS as specified on the building schedule.',
  },
  '2': {
    title: 'SS 2 — Emergency warning systems',
    body: 'Automatic or manual warning systems for fire or other dangers. Keep detection, panels, and alerting devices serviceable to the CS performance standard and reporting procedures.',
  },
  '2.1': {
    title: 'SS 2.1 — Fire alarm systems',
    body: 'Fire alarm / emergency warning: inspect panels, detectors, MCPs, sounders and any ASE links per the CS inspection and maintenance procedures.',
  },
  '3': {
    title: 'SS 3 — Spoken information for evacuation',
    body: 'Systems that communicate spoken information to facilitate evacuation. Verify intelligibility, coverage, and interfaces required by the CS.',
  },
  '3.1': {
    title: 'SS 3.1 — Automatic doors',
    body: 'Automatic doors on escape routes: inspect operation, fail-safe behaviour, and interfaces so egress is not impeded in an emergency.',
  },
  '3.2': {
    title: 'SS 3.2 — Access-controlled doors',
    body: 'Access-controlled doors must release for egress as required by the CS. Check locking hardware, power-fail behaviour, and interface with fire alarm where specified.',
  },
  '3.3': {
    title: 'SS 3.3 — Magnetic / hold-open devices',
    body: 'Electromagnetic holders and similar devices: confirm release on alarm/power conditions and that doors close and latch as required for fire/smoke separations.',
  },
  '4': {
    title: 'SS 4 — Emergency lighting systems',
    body: 'Emergency lighting and EXIT signs: inspect luminaires, battery duration, cleanliness/damage of lenses, and coverage on escape routes per CS frequency (often six-monthly and annual).',
  },
  '5': {
    title: 'SS 5 — Smoke control / pressurisation (as scheduled)',
    body: 'Where listed on the CS, inspect mechanical smoke control or related systems to the named performance standard and interface tests.',
  },
  '6': {
    title: 'SS 6 — Riser mains / fire service features (as coded)',
    body: 'Follow the exact SS number and procedures on this building’s Compliance Schedule. Inspect landing valves, signage, and accessibility for fire service use where applicable.',
  },
  '7': {
    title: 'SS 7 — Backflow preventers',
    body: 'Backflow preventers connected to potable water: test and maintain per CS and water-supply requirements; keep certificates/reporting as required.',
  },
  '8': {
    title: 'SS 8 — Lifts / escalators (as coded)',
    body: 'Passenger and related conveying systems: inspect to the CS performance standard and any fire-service lift features listed on the schedule.',
  },
  '8.1': {
    title: 'SS 8.1 — Passenger-carrying lifts',
    body: 'Passenger lifts: inspection and maintenance so the lift continues to meet its performance standard, including any emergency recall / firefighter features on the CS.',
  },
  '9': {
    title: 'SS 9 — Mechanical ventilation or air conditioning',
    body: 'HVAC systems listed on the CS: inspect plant, filters, controls, and fire/smoke interfaces (dampers, shutdown) per the building schedule procedures.',
  },
  '14': {
    title: 'SS 14 — Signs for specified systems',
    body: 'Signs associated with specified systems 1–13 (as on CS): verify presence, legibility, and correct location.',
  },
  '14.2': {
    title: 'SS 14.2 — Signs for specified systems 1–13',
    body: 'Signs relating to a system or feature in clauses 1 to 13 of the Compliance Schedule. They show how to operate the system, a caution, or where it is. Escape-route signs (running man, Exit, direction arrows) are SS 15.4.',
    sections: [
      {
        heading: 'What SS 14.2 is',
        text: 'Under the Building Act 2004 and the Compliance Schedule, SS 14.2 is signs relating to a system or feature specified in any of clauses 1 to 13. The sign identifies that system, shows how to operate it, or gives a warning about it.',
      },
      {
        heading: 'SS 14.2 and SS 15.4',
        points: [
          'SS 14.2 — signs for the systems in clauses 1–13: how to operate the system, a caution while it works, and its location or capacity.',
          'SS 15.4 — signs that guide the escape route itself: the green running man, Exit, and direction arrows.',
        ],
      },
      {
        heading: 'Signs by system',
        rows: [
          {
            ss: 'SS 2 — Fire alarm',
            sign: 'Manual call point',
            wording: 'FIRE ALARM — BREAK GLASS / OPERATE HERE. IN CASE OF FIRE DIAL 111.',
          },
          {
            ss: 'SS 3.2 — Access-controlled door',
            sign: 'Emergency door release',
            wording: 'EMERGENCY DOOR RELEASE — BREAK GLASS TO OPEN.',
          },
          {
            ss: 'SS 3.3 — Automatic fire or smoke door',
            sign: 'Fire-door warning',
            wording: 'FIRE DOOR — KEEP CLEAR / DO NOT OBSTRUCT. AUTOMATIC FIRE DOOR — STAND CLEAR.',
          },
          {
            ss: 'SS 8.1 / 8.2 — Passenger or goods lift',
            sign: 'Do not use in fire, and the load plate',
            wording: 'DO NOT USE LIFTS IN THE EVENT OF FIRE. MAX LOAD is the plate on that lift (for example 1000 kg / 13 persons).',
          },
          {
            ss: 'SS 8.3 — Escalator or moving walk',
            sign: 'Emergency stop',
            wording: 'EMERGENCY STOP.',
          },
          {
            ss: 'SS 1 — Sprinkler or gas suppression',
            sign: 'Control valve, and gas-discharge warning',
            wording: 'SPRINKLER CONTROL VALVE — DO NOT CLOSE. WARNING: CO2 / FM-200 GAS DISCHARGE AREA — EVACUATE ON ALARM.',
          },
          {
            ss: 'SS 6 — Fire-service inlet',
            sign: 'Inlet identification',
            wording: 'FIRE SERVICE INLET. DRY RISER INLET.',
          },
          {
            ss: 'SS 12 — Audio loop',
            sign: 'Hearing-loop symbol',
            wording: 'T-Coil hearing loop installed.',
          },
          {
            ss: 'SS 13.3 — Smoke curtain',
            sign: 'Keep the drop zone clear',
            wording: 'SMOKE CURTAIN — KEEP AREA BELOW CLEAR AT ALL TIMES.',
          },
        ],
      },
    ],
  },
  '15.2': {
    title: 'SS 15.2 — Final exits',
    body: 'Final exits and related hardware: inspect so egress is available, doors operate correctly, and any force/hardware limits on the CS are met.',
  },
  '15.4': {
    title: 'SS 15.4 — Signs to facilitate evacuation',
    body: 'Signs for communicating information intended to facilitate evacuation. They guide the escape route itself: the green running man, Exit, and direction arrows. Signs for how to operate systems 1–13 are SS 14.2. Fire separations are SS 15.3.',
    sections: [
      {
        heading: 'What SS 15.4 is',
        text: 'Under the Building Act 2004 and the Compliance Schedule, SS 15.4 is signs for communicating information intended to facilitate evacuation. The sign shows the escape route. Fire separations are SS 15.3. Smoke separations are SS 15.5.',
      },
      {
        heading: 'SS 15.4 and SS 14.2',
        points: [
          'SS 15.4 — signs that guide the escape route itself: the green running man, Exit, and direction arrows.',
          'SS 14.2 — signs for the systems in clauses 1–13: how to operate the system, a caution while it works, and its location or capacity.',
        ],
      },
      {
        heading: 'Signs on the escape route',
        rows: [
          {
            ss: 'Running man',
            sign: 'Exit symbol',
            wording: 'Green pictogram of a person running. It marks the exit.',
          },
          {
            ss: 'Exit sign',
            sign: 'The word at the exit',
            wording: 'EXIT.',
          },
          {
            ss: 'Direction arrow',
            sign: 'Which way to go',
            wording: 'An arrow, alone or with the running man, showing the escape route continues that way.',
          },
        ],
      },
    ],
  },
  '15.5': {
    title: 'SS 15.5 — Smoke separations',
    body: 'Smoke separations and smoke doors: inspect to prevent smoke passage and keep egress doors free to operate, per CS frequencies and procedures.',
  },
};

function normalizeCode(ssCode) {
  return String(ssCode || '')
    .replace(/^SS\s*/i, '')
    .replace(/\s+/g, '')
    .replace(/\//g, '.')
    .trim();
}

/**
 * @param {string} ssCode
 * @returns {{ title: string, body: string } | null}
 */
export function getSsHandbookInfo(ssCode) {
  const code = normalizeCode(ssCode);
  if (!code) return null;
  if (BY_CODE[code]) return BY_CODE[code];
  // Try parent (e.g. 15.2 → 15) only if exact missing
  const parent = code.split('.')[0];
  if (parent && BY_CODE[parent]) return BY_CODE[parent];
  return {
    title: `SS ${code}`,
    body: 'See the building Compliance Schedule procedures for this SS, and the MBIE Compliance Schedule Handbook for general inspection and maintenance expectations. Form 12A confirms the previous 12 months’ CS procedures were carried out.',
  };
}

export function formatSsTitle(ssCode, nameOrLocation) {
  const code = String(ssCode || '').trim();
  const label = String(nameOrLocation || '').trim();
  const codeDisp = code ? (code.toUpperCase().startsWith('SS') ? code : `SS ${code}`) : 'SS';
  if (!label) return codeDisp;
  const lower = label.toLowerCase();
  if (lower.startsWith('ss ') || (code && lower.startsWith(String(code).toLowerCase()))) return label;
  return `${codeDisp} ${label}`;
}

export default BY_CODE;
